import React from 'react';
import {
  Alert,
  Chip,
  Grid,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { formatLocalDate, formatLocalDateTime } from '../../utils/scheduleFormat';

const Stat = ({ label, value }) => (
  <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
    <Typography variant="body2" color="text.secondary">{label}</Typography>
    <Typography variant="h6" sx={{ wordBreak: 'break-word' }}>{value}</Typography>
  </Paper>
);

const ReplaceChip = ({ isReplaceable }) => (isReplaceable ? (
  <Chip label="Would be replaced" color="info" size="small" clickable={false} onClick={() => {}} />
) : (
  <Chip label="Kept" color="warning" size="small" clickable={false} onClick={() => {}} />
));

const conflictKey = (c) => `${c.existingGameId ?? `p${c.existingPlaceholderId}`}-${c.start}-${c.fieldId}`;

const TbdChip = () => (
  <Chip label="TBD" size="small" color="warning" variant="outlined" clickable={false} onClick={() => {}} />
);

const ExistingDescription = ({ conflict, showLeague = false }) => (
  <Stack direction="row" spacing={1} component="span" sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
    <span>
      {conflict.existingGameDescription}
      {showLeague && conflict.existingLeagueName ? ` (${conflict.existingLeagueName})` : ''}
    </span>
    {conflict.existingPlaceholderId != null && <TbdChip />}
  </Stack>
);

export const ConflictList = ({ conflicts = [] }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  if (isMobile) {
    return (
      <Stack spacing={1}>
        {conflicts.map((c) => (
          <Paper key={conflictKey(c)} variant="outlined" sx={{ p: 1.5 }} data-testid="conflict-card">
            <Typography variant="body2" sx={{ fontWeight: 600 }}>{formatLocalDateTime(c.start)}</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ wordBreak: 'break-word' }}>
              {[c.venueName, c.fieldName].filter(Boolean).join(' / ')}
            </Typography>
            <Typography variant="body2" component="div" sx={{ wordBreak: 'break-word' }}>
              <ExistingDescription conflict={c} showLeague />
            </Typography>
            <ReplaceChip isReplaceable={c.isReplaceable} />
          </Paper>
        ))}
      </Stack>
    );
  }

  return (
    <TableContainer component={Paper} variant="outlined">
      <Table size="small" aria-label="Field conflicts">
        <TableHead>
          <TableRow>
            <TableCell>When</TableCell>
            <TableCell>Field</TableCell>
            <TableCell>Existing game</TableCell>
            <TableCell>League</TableCell>
            <TableCell>Status</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {conflicts.map((c) => (
            <TableRow key={conflictKey(c)}>
              <TableCell>{formatLocalDateTime(c.start)}</TableCell>
              <TableCell>{[c.venueName, c.fieldName].filter(Boolean).join(' / ')}</TableCell>
              <TableCell sx={{ wordBreak: 'break-word' }}><ExistingDescription conflict={c} /></TableCell>
              <TableCell>{c.existingLeagueName || '–'}</TableCell>
              <TableCell><ReplaceChip isReplaceable={c.isReplaceable} /></TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

const ScheduleVenuesAndDates = ({ result, approval, isApproved = false }) => {
  const fieldUsage = result?.fieldUsage || [];
  const extraGameCount = result?.extraGameCount ?? (result?.extraGames || []).length;
  const tbdGameCount = result?.tbdGameCount ?? 0;
  const statSize = { xs: 12, sm: 6, md: 'grow' };
  const blackoutWeeks = (result?.weeks || []).filter((w) => w.isBlackout);

  return (
    <Stack spacing={3}>
      <Grid container spacing={2}>
        <Grid size={statSize}>
          <Stat label="First game" value={formatLocalDate(result?.firstGameDate) || '–'} />
        </Grid>
        <Grid size={statSize}>
          <Stat label="Last game" value={formatLocalDate(result?.lastGameDate) || '–'} />
        </Grid>
        <Grid size={statSize}>
          <Stat label="Game weeks" value={result?.playableWeeks ?? 0} />
        </Grid>
        <Grid size={statSize}>
          <Stat label="Games / byes" value={`${result?.totalGames ?? 0} / ${result?.totalByes ?? 0}`} />
        </Grid>
        {extraGameCount > 0 && (
          <Grid size={statSize}>
            <Stat label="Other games" value={tbdGameCount > 0 ? `${extraGameCount} (${tbdGameCount} TBD)` : extraGameCount} />
          </Grid>
        )}
      </Grid>

      <Stack spacing={1}>
        <Typography variant="h6">Field usage</Typography>
        {fieldUsage.length === 0 ? (
          <Typography variant="body2" color="text.secondary">No regular-season games scheduled.</Typography>
        ) : (
          <TableContainer component={Paper} variant="outlined">
            <Table size="small" aria-label="Field usage">
              <TableHead>
                <TableRow>
                  <TableCell>Venue</TableCell>
                  <TableCell>Field</TableCell>
                  <TableCell align="right">Games</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {fieldUsage.map((f) => (
                  <TableRow key={`${f.venueId}-${f.fieldId ?? 'any'}`}>
                    <TableCell sx={{ wordBreak: 'break-word' }}>{f.venueName}</TableCell>
                    <TableCell sx={{ wordBreak: 'break-word' }}>{f.fieldName || 'Any field'}</TableCell>
                    <TableCell align="right">{f.games}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Stack>

      <Stack spacing={1}>
        <Typography variant="h6">Blackout weeks</Typography>
        {blackoutWeeks.length === 0 ? (
          <Typography variant="body2" color="text.secondary">No weeks skipped.</Typography>
        ) : (
          <Stack spacing={0.5}>
            {blackoutWeeks.map((w) => (
              <Typography key={w.weekStartDate} variant="body2">
                {`Week of ${formatLocalDate(w.weekStartDate)}${w.blackoutLabel ? ` – ${w.blackoutLabel}` : ''}`}
              </Typography>
            ))}
          </Stack>
        )}
      </Stack>

      {!isApproved && approval && (
        <Stack spacing={1}>
          <Typography variant="h6">Approval impact</Typography>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 4 }}>
              <Stat label="Replaceable future games" value={approval.replaceableGameCount ?? 0} />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <Stat label="Existing games kept in range" value={approval.keptGamesInRangeCount ?? 0} />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <Stat label="Placeholders removed" value={approval.removablePlaceholderCount ?? 0} />
            </Grid>
          </Grid>
          <Typography variant="body2" color="text.secondary">
            Replaceable games are this league&apos;s future regular-season games with no score, match reports or appearances.
            Only schedule placeholders on the same date, time and field as a generated game are removed on approval; other placeholders are kept.
          </Typography>
          {(approval.conflicts || []).length === 0 ? (
            <Alert severity="success">No field clashes with existing games or TBD games.</Alert>
          ) : (
            <>
              <Alert severity="warning">
                {`${approval.conflictCount ?? approval.conflicts.length} generated game(s) clash with existing games or TBD games on the same field.`}
                {(approval.conflictCount ?? 0) > approval.conflicts.length ? ` Showing the first ${approval.conflicts.length}.` : ''}
              </Alert>
              <ConflictList conflicts={approval.conflicts} />
            </>
          )}
        </Stack>
      )}
    </Stack>
  );
};

export default ScheduleVenuesAndDates;
